import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { 
  fetchVideoComments, 
  addVideoComment,
  selectVideoComments,
  selectCommentsLoading
} from '../../store/slices/videoSlice';
import VideoComments from '../organisms/VideoComments';

interface CommentModalProps {
  videoId: string;
  isOpen: boolean;
  onClose: () => void;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const ModalContent = styled.div`
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  background-color: #1C1628;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  animation: slideUp 0.3s ease;
  
  @keyframes slideUp {
    from {
      transform: translateY(50px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h3`
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  
  &:hover {
    color: #fff;
  }
`;

const ModalBody = styled.div`
  padding: 16px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: rgba(255, 255, 255, 0.6);
`;

const CommentModal: React.FC<CommentModalProps> = ({ videoId, isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const comments = useAppSelector(selectVideoComments);
  const loading = useAppSelector(selectCommentsLoading);
  const modalRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (isOpen && videoId) {
      dispatch(fetchVideoComments(videoId));
    }
  }, [isOpen, videoId, dispatch]);
  
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node) && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    
    // Prevent body scrolling when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);
  
  const handleAddComment = (videoId: string, content: string) => {
    dispatch(addVideoComment({ videoId, content }));
  };
  
  const videoComments = comments[videoId] || [];
  
  return (
    <ModalOverlay $isOpen={isOpen}>
      <ModalContent ref={modalRef}>
        <ModalHeader>
          <ModalTitle>Comments</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <LoadingContainer>Loading comments...</LoadingContainer>
          ) : (
            <VideoComments
              comments={videoComments}
              videoId={videoId}
              onAddComment={handleAddComment}
            />
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default CommentModal;
